const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8317';

export interface ContactRequest {
  id?: number;
  name: string;
  email: string;
  company?: string;
  message: string;
  created_at?: string;
}

export interface NewsletterSubscription {
  id?: number;
  email: string;
  created_at?: string;
}

export interface ContactResponse {
  id: number;
  message: string;
}

export interface AdminCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async submitContact(contact: ContactRequest): Promise<ContactResponse> {
    const response = await fetch(`${this.baseUrl}/api/contacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contact),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to submit contact form');
    }

    return response.json();
  }

  async adminLogin(credentials: AdminCredentials): Promise<LoginResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    return response.json();
  }

  async getContacts(token: string, page: number = 1, perPage: number = 10): Promise<PaginatedResponse<ContactRequest>> {
    const response = await fetch(
      `${this.baseUrl}/api/admin/contacts?page=${page}&per_page=${perPage}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch contacts');
    }

    return response.json();
  }

  async subscribeNewsletter(data: { email: string }): Promise<{ id: number; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/newsletter/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to subscribe to newsletter');
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();